class SkipperBookingCalendar extends HTMLElement {
  async connectedCallback() {
    this.today = new Date();
    this.today.setHours(0, 0, 0, 0);
    this.viewDate = new Date(this.today);
    this.selectedDate = null;
    this.selectedTime = null;

    this.availableDays = (this.dataset.availableDays || '3,4')
      .split(',').map(Number);
    this.timeSlots = (this.dataset.timeSlots || '08:00,09:00,10:00,11:00,12:00,13:00,14:00,15:00,16:00,17:00')
      .split(',').map(s => s.trim());
    this.bookingProductHandle = this.dataset.productHandle || 'fitment-booking';
    this.leadDays = parseInt(this.dataset.leadDays || '2', 10);

    this.form = this.querySelector('.skipper-booking-cal__form');
    this.calGrid = this.querySelector('.skipper-booking-cal__grid');
    this.monthLabel = this.querySelector('.skipper-booking-cal__month');
    this.prevBtn = this.querySelector('.skipper-booking-cal__nav--prev');
    this.nextBtn = this.querySelector('.skipper-booking-cal__nav--next');
    this.timesPanel = this.querySelector('.skipper-booking-cal__times');
    this.submitBtn = this.querySelector('.skipper-booking-cal__submit');
    this.confirmPanel = this.querySelector('.skipper-booking-cal__confirm');
    this.hiddenDate = this.querySelector('[name="properties[Booking Date]"]');
    this.hiddenTime = this.querySelector('[name="properties[Booking Time]"]');

    this.prevBtn.addEventListener('click', () => { this.viewDate.setMonth(this.viewDate.getMonth() - 1); this.renderCalendar(); });
    this.nextBtn.addEventListener('click', () => { this.viewDate.setMonth(this.viewDate.getMonth() + 1); this.renderCalendar(); });
    this.form.addEventListener('submit', (e) => this.handleSubmit(e));

    await this.fetchVariants();
    this.renderCalendar();
  }

  async fetchVariants() {
    try {
      const res = await fetch(`/products/${this.bookingProductHandle}.js`);
      if (!res.ok) { this.variantMap = {}; return; }
      const product = await res.json();
      this.defaultVariantId = product.variants[0].id;
      this.variantMap = {};
      product.variants.forEach(v => {
        this.variantMap[v.title.toLowerCase().trim()] = {
          id: v.id,
          available: v.available
        };
      });
    } catch (e) {
      this.variantMap = {};
    }
  }

  slotKey(date, slot) {
    const day = date.toLocaleDateString('en-AU', { weekday: 'long' }).toLowerCase();
    return `${day} ${slot}`;
  }

  isDayFullyBooked(date) {
    if (!this.variantMap || Object.keys(this.variantMap).length === 0) return false;
    return this.timeSlots.every(slot => {
      const variant = this.variantMap[this.slotKey(date, slot)];
      return variant && !variant.available;
    });
  }

  isAvailable(date) {
    const minDate = new Date(this.today);
    minDate.setDate(minDate.getDate() + this.leadDays);
    if (date < minDate) return false;
    if (!this.availableDays.includes(date.getDay())) return false;
    return !this.isDayFullyBooked(date);
  }

  renderCalendar() {
    const year = this.viewDate.getFullYear();
    const month = this.viewDate.getMonth();
    this.monthLabel.textContent = new Date(year, month, 1).toLocaleDateString('en-AU', { month: 'long', year: 'numeric' });

    const firstDay = new Date(year, month, 1).getDay(); // 0=Sun,1=Mon..6=Sat
    const daysInMonth = new Date(year, month + 1, 0).getDate();
    const startOffset = (firstDay >= 1 && firstDay <= 5) ? firstDay - 1 : 0;

    this.calGrid.innerHTML = '';
    ['Mon', 'Tue', 'Wed', 'Thu', 'Fri'].forEach(d => {
      const h = document.createElement('div');
      h.className = 'skipper-cal__day-header';
      h.textContent = d;
      this.calGrid.appendChild(h);
    });

    for (let i = 0; i < startOffset; i++) {
      const blank = document.createElement('div');
      blank.className = 'skipper-cal__day skipper-cal__day--blank';
      this.calGrid.appendChild(blank);
    }

    for (let d = 1; d <= daysInMonth; d++) {
      const date = new Date(year, month, d);
      if (date.getDay() === 0 || date.getDay() === 6) continue;

      const btn = document.createElement('button');
      btn.type = 'button';
      btn.className = 'skipper-cal__day';
      btn.textContent = d;

      if (!this.availableDays.includes(date.getDay()) || date < (() => { const m = new Date(this.today); m.setDate(m.getDate() + this.leadDays); return m; })()) {
        btn.disabled = true;
        btn.classList.add('skipper-cal__day--unavailable');
      } else if (this.isDayFullyBooked(date)) {
        btn.disabled = true;
        btn.classList.add('skipper-cal__day--full');
        btn.title = 'Fully booked';
      } else {
        btn.classList.add('skipper-cal__day--available');
        if (this.selectedDate && date.toDateString() === this.selectedDate.toDateString()) {
          btn.classList.add('skipper-cal__day--selected');
        }
        btn.addEventListener('click', () => this.selectDate(date));
      }

      this.calGrid.appendChild(btn);
    }
  }

  selectDate(date) {
    this.selectedDate = date;
    this.selectedTime = null;
    this.hiddenDate.value = date.toLocaleDateString('en-AU', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' });
    this.hiddenTime.value = '';
    this.renderCalendar();
    this.renderTimes();
    this.updateConfirm();
  }

  renderTimes() {
    this.timesPanel.innerHTML = '';
    const heading = document.createElement('p');
    heading.className = 'skipper-booking-cal__times-heading';
    heading.textContent = 'Pick a time';
    this.timesPanel.appendChild(heading);

    const grid = document.createElement('div');
    grid.className = 'skipper-booking-cal__times-grid';

    this.timeSlots.forEach(slot => {
      const variant = this.variantMap ? this.variantMap[this.slotKey(this.selectedDate, slot)] : null;
      const isFull = variant && !variant.available;

      const btn = document.createElement('button');
      btn.type = 'button';
      btn.className = 'skipper-time-slot';
      if (isFull) btn.classList.add('skipper-time-slot--full');
      if (this.selectedTime === slot) btn.classList.add('skipper-time-slot--selected');
      btn.disabled = isFull;

      const [h, m] = slot.split(':');
      const hour = parseInt(h, 10);
      const label = `${hour > 12 ? hour - 12 : hour === 0 ? 12 : hour}:${m}${hour >= 12 ? 'pm' : 'am'}`;
      btn.innerHTML = isFull ? `${label}<span class="skipper-time-slot__full-tag">Full</span>` : label;

      if (!isFull) btn.addEventListener('click', () => this.selectTime(slot, btn));
      grid.appendChild(btn);
    });

    this.timesPanel.appendChild(grid);
    this.timesPanel.classList.remove('hidden');
  }

  selectTime(slot, btn) {
    this.selectedTime = slot;
    this.hiddenTime.value = slot;
    this.timesPanel.querySelectorAll('.skipper-time-slot').forEach(b => b.classList.remove('skipper-time-slot--selected'));
    btn.classList.add('skipper-time-slot--selected');
    this.updateConfirm();
  }

  updateConfirm() {
    if (!this.selectedDate || !this.selectedTime) {
      this.confirmPanel.classList.add('hidden');
      this.submitBtn.disabled = true;
      return;
    }
    const [h, m] = this.selectedTime.split(':');
    const hour = parseInt(h, 10);
    const timeStr = `${hour > 12 ? hour - 12 : hour === 0 ? 12 : hour}:${m}${hour >= 12 ? 'pm' : 'am'}`;
    const dateStr = this.selectedDate.toLocaleDateString('en-AU', { weekday: 'long', day: 'numeric', month: 'long' });
    this.confirmPanel.querySelector('.skipper-confirm__datetime').textContent = `${dateStr} at ${timeStr}`;
    this.confirmPanel.classList.remove('hidden');
    this.submitBtn.disabled = false;
  }

  async handleSubmit(e) {
    e.preventDefault();

    const name = this.form.querySelector('[name="properties[Name]"]').value.trim();
    const phone = this.form.querySelector('[name="properties[Phone]"]').value.trim();
    const email = this.form.querySelector('[name="properties[Email]"]').value.trim();
    const vessel = this.form.querySelector('[name="properties[Vessel / Vehicle]"]').value.trim();
    const location = this.form.querySelector('[name="properties[Location]"]').value.trim();

    if (!name || !phone || !email || !vessel || !location || !this.selectedDate || !this.selectedTime) {
      this.showError('Please fill in all fields and select a date and time.');
      return;
    }

    this.submitBtn.textContent = 'Securing your spot...';
    this.submitBtn.disabled = true;

    try {
      const key = this.slotKey(this.selectedDate, this.selectedTime);
      const variant = this.variantMap && this.variantMap[key];

      if (variant && !variant.available) {
        throw new Error('That slot just filled up — please pick another time.');
      }

      const variantId = variant ? variant.id : this.defaultVariantId;
      if (!variantId) throw new Error('Booking product not found. Please contact us directly.');

      const properties = {};
      new FormData(this.form).forEach((v, k) => {
        if (k.startsWith('properties[')) properties[k.slice(11, -1)] = v;
      });

      const addRes = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id: variantId, quantity: 1, properties })
      });

      if (!addRes.ok) throw new Error('Could not add booking to cart.');
      window.location.href = '/cart';
    } catch (err) {
      this.showError(err.message || 'Something went wrong. Please try again or call us.');
      this.submitBtn.textContent = 'Secure My Spot — $99';
      this.submitBtn.disabled = false;
    }
  }

  showError(msg) {
    let el = this.querySelector('.skipper-booking-cal__error');
    if (!el) {
      el = document.createElement('p');
      el.className = 'skipper-booking-cal__error';
      this.submitBtn.insertAdjacentElement('beforebegin', el);
    }
    el.textContent = msg;
    el.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
  }
}

customElements.define('skipper-booking-calendar', SkipperBookingCalendar);
